import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Stack, Typography, Chip, LinearProgress, Alert, Button, Divider,
  Paper, CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import LockIcon from '@mui/icons-material/Lock';

import { getCategory } from '../api/categoryApi';
import { getPatientHistory } from '../api/patientApi';
import { getActiveAssessment, startAssessment, confirmAssessmentPayment, finishAssessment } from '../api/saasApi';
import { openRazorpayCheckout } from '../utils/razorpay';
import { usePatient, formatAge } from '../hooks/usePatient';
import { useMe } from '../context/MeContext';
import { getDomainProgress, getDomainProgressbyName } from '../components/CategoryLevels';
import { PageHeader, SectionCard, CardsSkeleton } from '../components/ui';
import { level as levelTokens } from '../theme/tokens';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { useSnackbar } from '../components/Snackbar';
import { orderOf, scoredDomainKeys, isDomainScored } from '../utils/assessmentProgress';

/** The two short domains; worth signalling so the clinician knows what they're in for. */
const SMALL_DOMAINS = { SEN: 11, BEH: 7 };

const money = (amount, currency) => {
  const n = Number(amount) || 0;
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR', maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${currency || 'INR'} ${n}`;
  }
};

const Domains = () => {
  const navigate = useNavigate();
  const { patientId, patient, loading: patientLoading, error: patientError } = usePatient();
  const { billing, isExempt, isPlatformAdmin } = useMe();

  const [domains, setDomains] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Per-assessment payment gate. A real clinic must have a Paid (unfinished)
  // assessment for this patient before scoring. The legacy tenant is exempt.
  const gated = !!billing && !isExempt && !isPlatformAdmin;
  const [assessment, setAssessment] = useState(null);
  const [gateLoading, setGateLoading] = useState(gated);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');
  const { showSnackbar } = useSnackbar();
  const [confirmEndOpen, setConfirmEndOpen] = useState(false);
  const [ending, setEnding] = useState(false);

  useEffect(() => {
    if (!patientId) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const [cats, hist] = await Promise.all([getCategory(), getPatientHistory(patientId)]);
        if (cancelled) return;
        setDomains(cats.data?.data || []);
        setHistory(hist.data?.data || []);
      } catch (e) {
        if (!cancelled) setError('Could not load the domain list.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [patientId]);

  useEffect(() => {
    if (!patientId || !gated) { setGateLoading(false); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const res = await getActiveAssessment(patientId);
        if (!cancelled) setAssessment(res.data?.data || null);
      } catch (e) {
        // Non-fatal: fall through to the pay gate rather than blocking the page.
        if (!cancelled) setAssessment(null);
      } finally {
        if (!cancelled) setGateLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [patientId, gated]);

  const isPaid = assessment?.status === 'Paid';
  const needsPayment = gated && !isPaid;

  const handleStartPay = async () => {
    setPaying(true);
    setPayError('');
    try {
      const res = await startAssessment(patientId);
      if (res.data?.succeeded === false) {
        setPayError(res.data.messages?.[0] || 'Could not start the assessment.');
        return;
      }
      const d = res.data.data;
      if (d.isPaid) {
        setAssessment({ id: d.assessmentId, status: 'Paid', submissionGroupId: d.submissionGroupId });
        return;
      }
      const rp = await openRazorpayCheckout({
        keyId: d.razorpayKeyId,
        orderId: d.razorpayOrderId,
        amount: d.amount,
        currency: d.currency,
        name: patient?.name ? `Assessment · ${patient.name}` : 'Vqura Assessment',
        description: 'Developmental screening',
        prefill: {},
      });
      const conf = await confirmAssessmentPayment(d.assessmentId, rp.razorpay_payment_id, rp.razorpay_signature);
      if (conf.data?.succeeded === false) {
        setPayError(conf.data.messages?.[0] || 'Payment verification failed.');
        return;
      }
      setAssessment({ id: d.assessmentId, status: 'Paid' });
      try {
        const fresh = await getActiveAssessment(patientId);
        if (fresh.data?.data) setAssessment(fresh.data.data);
      } catch (e) { /* keep the minimal record */ }
    } catch (e) {
      setPayError(e?.message || e?.response?.data?.messages?.[0] || 'Payment could not be completed.');
    } finally {
      setPaying(false);
    }
  };

  const cards = useMemo(() => {
    const latest = [...history].sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))[0];
    const achievedBy = {};
    (latest?.domains || []).forEach((d) => { achievedBy[d.domain] = d.skillsAchieved; });

    return [...domains]
      .sort((a, b) => orderOf(a.code) - orderOf(b.code))
      .map((cat) => {
        const achieved = achievedBy[cat.name] ?? achievedBy[cat.code];
        const assessed = achieved !== undefined;
        const progress = assessed
          ? getDomainProgressbyName(cat.name, achieved) || getDomainProgress(cat.code, achieved)
          : null;

        return {
          code: cat.code,
          name: cat.name,
          questionCount: SMALL_DOMAINS[cat.code] || 45,
          assessed,
          achieved: achieved ?? 0,
          level: progress?.level ?? null,
          percentage: progress ? Number(progress.percentage) : null,
        };
      });
  }, [domains, history]);

  const done = cards.filter((c) => c.assessed).length;

  // Domains scored inside the current paid assessment (used for the end warning).
  const scoredInAssessment = useMemo(() => {
    if (!isPaid) return 0;
    const keys = scoredDomainKeys(history, assessment);
    return domains.filter((c) => isDomainScored(keys, c)).length;
  }, [isPaid, history, assessment, domains]);

  const endAssessmentAndOpenReport = async () => {
    if (!assessment?.id) return;
    setEnding(true);
    try {
      const res = await finishAssessment(assessment.id);
      if (res.data?.succeeded === false) {
        showSnackbar(res.data?.messages?.[0] || 'Could not end the assessment.', 'error');
        return;
      }
      setConfirmEndOpen(false);
      navigate(`/patient/${patientId}/report`);
    } catch (e) {
      showSnackbar(e?.response?.data?.messages?.[0] || 'Could not end the assessment.', 'error');
    } finally {
      setEnding(false);
    }
  };

  if (patientError) {
    return (
      <Stack spacing={2} alignItems="flex-start">
        <Alert severity="warning">{patientError}</Alert>
        <Button onClick={() => navigate('/patient')}>Back to patients</Button>
      </Stack>
    );
  }

  // ---- Payment gate: shown before the domain list for gated clinics ----
  if (gateLoading) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
    );
  }

  if (needsPayment) {
    const price = billing?.assessmentPrice;
    const currency = billing?.currency || 'INR';
    return (
      <>
        <PageHeader
          title="Start an assessment"
          subtitle={
            patientLoading
              ? 'Loading patient…'
              : `${patient?.name || 'Patient'}${patient?.dateOfBirth ? ` · ${formatAge(patient.dateOfBirth)}` : ''}`
          }
        />
        <Paper sx={{ p: { xs: 3, sm: 4 }, maxWidth: 560, mx: 'auto', textAlign: 'center' }}>
          <LockIcon color="action" sx={{ fontSize: 44, mb: 1 }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Payment required
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Pay for this assessment to begin scoring {patient?.name || 'this child'} across all
            developmental domains. One payment covers the full screening; you can score across
            multiple sittings and resume any time before you finish.
          </Typography>

          <Box sx={{ my: 3 }}>
            <Typography variant="overline" color="text.secondary" display="block">Assessment fee</Typography>
            <Typography variant="h3" fontWeight={800}>{money(price, currency)}</Typography>
          </Box>

          {payError && <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }} onClose={() => setPayError('')}>{payError}</Alert>}

          <Button
            variant="contained"
            size="large"
            fullWidth
            disabled={paying}
            onClick={handleStartPay}
            startIcon={paying ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {paying ? 'Processing…' : 'Pay & start assessment'}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
            Payments are processed securely by Razorpay.
          </Typography>
          <Button sx={{ mt: 1 }} onClick={() => navigate(`/patient/${patientId}`)}>Cancel</Button>
        </Paper>
      </>
    );
  }

  // ---- Domain list (assessment paid, or exempt/platform) ----
  return (
    <>
      <PageHeader
        title="Developmental domains"
        subtitle={
          patientLoading
            ? 'Loading patient…'
            : `${patient?.name || 'Patient'}${patient?.dateOfBirth ? ` · ${formatAge(patient.dateOfBirth)}` : ''} · ${done} of ${cards.length || 10} assessed`
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            {isPaid && <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Assessment paid" />}
            {isPaid ? (
              scoredInAssessment > 0 && (
                <Button
                  variant="outlined"
                  startIcon={<DescriptionIcon />}
                  onClick={() => setConfirmEndOpen(true)}
                >
                  Finish &amp; view report
                </Button>
              )
            ) : (
              done > 0 && (
                <Button
                  variant="outlined"
                  startIcon={<DescriptionIcon />}
                  onClick={() => navigate(`/patient/${patientId}/report`)}
                >
                  Progress report
                </Button>
              )
            )}
          </Stack>
        }
      />

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {isPaid && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Assessment in progress — {scoredInAssessment} of {domains.length || 10} domains scored.
          You can score across sittings. <strong>Finish &amp; view report</strong> ends this assessment.
        </Alert>
      )}

      <ConfirmDialog
        open={confirmEndOpen}
        title="End this assessment?"
        body={`${scoredInAssessment} of ${domains.length || 10} domains scored. Viewing the progress report ends this paid assessment — you won't be able to score more domains, and the next assessment will need a new payment.`}
        confirmLabel="End assessment & view report"
        busy={ending}
        onConfirm={endAssessmentAndOpenReport}
        onClose={() => setConfirmEndOpen(false)}
      />

      <SectionCard
        title="Choose a domain"
        subtitle="Each domain is scored separately and can be revisited at any time"
      >
        {loading ? (
          <CardsSkeleton count={3} height={132} />
        ) : (
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' } }}>
            {cards.map((d) => {
              const tone = d.level ? levelTokens[d.level] : null;
              const complete = d.assessed && d.achieved >= d.questionCount;

              return (
                <Box
                  key={d.code}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/patient/${patientId}/assess/${d.code}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/patient/${patientId}/assess/${d.code}`);
                    }
                  }}
                  sx={{
                    p: 2.5, borderRadius: 1, cursor: 'pointer',
                    border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
                    transition: (t) => `border-color ${t.transitions.duration.shorter}ms, box-shadow ${t.transitions.duration.shorter}ms`,
                    '&:hover': { borderColor: 'primary.main', boxShadow: 1 },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="overline" color="text.secondary">{d.code}</Typography>
                      <Typography variant="h4" sx={{ mt: -0.5 }}>{d.name}</Typography>
                    </Box>
                    {complete
                      ? <CheckCircleIcon color="success" fontSize="small" />
                      : d.level
                        ? <Chip size="small" label={`L${d.level}`} sx={{ bgcolor: tone.light, color: tone.main, border: '1px solid', borderColor: tone.main }} />
                        : null}
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
                    <Typography variant="caption" color="text.secondary">
                      {d.assessed ? `${d.achieved} of ${d.questionCount} skills achieved` : `${d.questionCount} questions`}
                    </Typography>
                    <Typography variant="caption" fontWeight={600} color={d.assessed ? 'text.primary' : 'text.disabled'}>
                      {d.assessed ? 'In progress' : 'Not started'}
                    </Typography>
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={d.assessed ? Math.min(100, (d.achieved / d.questionCount) * 100) : 0}
                    sx={{
                      height: 6, borderRadius: 3, bgcolor: 'grey.100',
                      '& .MuiLinearProgress-bar': { bgcolor: tone ? tone.main : 'grey.300', borderRadius: 3 },
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </SectionCard>
    </>
  );
};

export default Domains;
